/**
 * scopeRecords (#2211) — restrict the fact-sourced FEED records (the ledger +
 * category + unsorted-chip source) to a single bank account, so selecting a Konto
 * re-scopes EVERYTHING below the chart, not just the headline + balance curve.
 *
 * Routing mirrors ``partitionEngineRecords`` so the feed under account X shows the
 * exact rows that build X's balance curve:
 *  - a record with an explicit ``account_id`` matching a real account → THAT account
 *    (bank rows carry it on the wire — the worker stamps ``cash_flow.account_id``,
 *    an ontology-required FK, onto every bank fact);
 *  - every OTHER record — account-less user plans (the write path has no account
 *    dimension yet), account-less native/legacy rows, and the defensive case of an
 *    ``account_id`` that matches no real account — folds onto the ``isOverlayPrimary``
 *    account (D2: the ``fa-liquiditaet`` alias if present, else the input-first
 *    account — fixed BEFORE the #2089 A–Z display sort, so re-sorting the list for
 *    the view never re-routes a row).
 *
 * Because every record therefore has exactly ONE owning account among the real
 * accounts, Σ(per-account feeds) ≡ the Alle-Konten feed — no row vanishes under
 * every selection, and none is double-counted.
 *
 * The Alle-Konten aggregate (and any non-account id — mirroring ``resolveKonto``'s
 * tolerant resolve) returns the INPUT REFERENCE unchanged: a specific-account scope
 * is the only case that allocates a new (filtered) array. Returning the same
 * reference under Alle Konten keeps the page byte-identical to today for
 * single-account tenants (hard-forced to the aggregate) and stops ``LedgerFeed``'s
 * ``[rows]``-keyed effects from spuriously resetting.
 */
import type { LiquidityAccount } from './accounts';
import type { LiquiditaetRecord } from './types';

export function scopeRecordsToAccount(
  records: LiquiditaetRecord[],
  kontoId: string,
  accounts: LiquidityAccount[],
): LiquiditaetRecord[] {
  // Alle Konten (or any non-account id) → the input reference, unfiltered.
  if (!accounts.some((a) => a.id === kontoId)) return records;

  const accountIds = new Set(accounts.map((a) => a.id));
  const overlayId = accounts.find((a) => a.isOverlayPrimary)?.id ?? accounts[0]?.id;
  // A record's owning account: its own when that account exists, else the overlay
  // primary — so the partition is total (Σ per-account ≡ whole; no orphan).
  const ownerOf = (r: LiquiditaetRecord): string | undefined =>
    r.account_id && accountIds.has(r.account_id) ? r.account_id : overlayId;

  return records.filter((r) => ownerOf(r) === kontoId);
}
